import { Component, OnChanges, Input, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, FormArray } from '@angular/forms';
import { FormlyFieldConfig, FormlyOptions } from './formly.field.config';
import { FormlyFormBuilder } from '../services/formly.form.builder';
import { assignModelValue, isNullOrUndefined, isObject, reverseDeepMerge, getKey, getValueForKey, getFieldModel } from '../utils';
export class FormlyForm implements OnChanges {
   model: any = {};
   form: FormGroup = new FormGroup({});
   fields: FormlyFieldConfig[] = [];
   options: FormlyOptions;
/**
 * \@internal
 */
buildForm: boolean = true;
private initialModel: any;
/**
 * @param {?} formlyBuilder
 */
constructor(private formlyBuilder: FormlyFormBuilder) {}
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    if (changes.fields) {
      this.model = this.model || {};
      this.form = this.form || (new FormGroup({}));
      this.setOptions();
      if (this.buildForm !== false) {
        this.formlyBuilder.buildForm(this.form, this.fields, this.model, this.options);
      }
      this.updateInitialValue();
    } else if (changes.model && this.fields && this.fields.length > 0) {
      this.form.patchValue(this.model);
    }
  }
/**
 * @param {?} field
 * @return {?}
 */
fieldModel(field: FormlyFieldConfig) {
    if (field.key && (field.fieldGroup || field.fieldArray)) {
      return getFieldModel(this.model, field, true);
    }
    return this.model;
  }
/**
 * @param {?} event
 * @return {?}
 */
changeModel(event: { key: string, value: any }) {
    assignModelValue(this.model, event.key, event.value);
  }
/**
 * @return {?}
 */
setOptions() {
    this.options = this.options || {};
    if (!this.options.resetModel) {
      this.options.resetModel = this.resetModel.bind(this);
    }
    if (!this.options.updateInitialValue) {
      this.options.updateInitialValue = this.updateInitialValue.bind(this);
    }
  }
/**
 * @param {?=} model
 * @return {?}
 */
private resetModel(model?: any) {
    model = isNullOrUndefined(model) ? this.initialModel : model;
    this.form.patchValue(model);
    this.resetFormGroup(model, this.form);
    this.resetFormModel(model, this.model);
  }
/**
 * @param {?} model
 * @param {?} formModel
 * @param {?=} path
 * @return {?}
 */
private resetFormModel(model: any, formModel: any, path?: (string | number)[]) {
    if (!isObject(model) && !Array.isArray(model)) {
      return;
    }

    // removes
    for (let /** @type {?} */ key in formModel) {
      if (!(key in model) || isNullOrUndefined(model[key])) {
        if (!this.form.get((path || []).concat(key))) {
          // don't remove if bound to a control
          delete formModel[key];
        }
      }
    }

    // inserts and updates
    for (let /** @type {?} */ key in model) {
      if (!isNullOrUndefined(model[key])) {
        if (key in formModel) {
          this.resetFormModel(model[key], formModel[key], (path || []).concat(key));
        }
        else {
          formModel[key] = model[key];
        }
      }
    }
  }
/**
 * @param {?} model
 * @param {?} form
 * @param {?=} actualKey
 * @return {?}
 */
private resetFormGroup(model: any, form: FormGroup, actualKey?: string) {
    for (let /** @type {?} */ controlKey in form.controls) {
      let /** @type {?} */ key = getKey(controlKey, actualKey);
      if (form.controls[controlKey] instanceof FormGroup) {
        this.resetFormGroup(model, /** @type {?} */(( <FormGroup>form.controls[controlKey])), key);
      }
      if (form.controls[controlKey] instanceof FormArray) {
        this.resetFormArray(model, /** @type {?} */(( <FormArray>form.controls[controlKey])), key);
      }
      if (form.controls[controlKey] instanceof FormControl) {
        form.controls[controlKey].setValue(getValueForKey(model, key));
      }
    }
  }
/**
 * @param {?} model
 * @param {?} formArray
 * @param {?} key
 * @return {?}
 */
private resetFormArray(model: any, formArray: FormArray, key: string) {
    let /** @type {?} */ newValue = getValueForKey(model, key);

    // removes and updates
    for (let /** @type {?} */ i = formArray.length - 1; i >= 0; i--) {
      if (formArray.at(i) instanceof FormGroup) {
        if (newValue && !isNullOrUndefined(newValue[i])) {
          this.resetFormGroup(newValue[i], /** @type {?} */(( <FormGroup>formArray.at(i))));
        }
        else {
          formArray.removeAt(i);
          let /** @type {?} */ value = getValueForKey(this.model, key);
          if (Array.isArray(value)) {
            value.splice(i, 1);
          }
        }
      }
    }

    // inserts
    if (Array.isArray(newValue) && formArray.length < newValue.length) {
      let /** @type {?} */ remaining = newValue.length - formArray.length;
      let /** @type {?} */ initialLength = formArray.length;
      for (let /** @type {?} */ i = 0; i < remaining; i++) {
        let /** @type {?} */ pos = initialLength + i;
        getValueForKey(this.model, key).push(newValue[pos]);
        formArray.push(new FormGroup({}));
      }
    }
  }
/**
 * @return {?}
 */
private updateInitialValue() {
    let /** @type {?} */ obj = reverseDeepMerge(this.form.value, this.model);
    this.initialModel = JSON.parse(JSON.stringify(obj));
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'formly-form',
  template: `
    <formly-field *ngFor="let field of fields"
      [model]="fieldModel(field)" [form]="form"
      [field]="field" (modelChange)="changeModel($event)"
      [ngClass]="field.className"
      [options]="options">
    </formly-field>
    <ng-content></ng-content>
  `,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: FormlyFormBuilder, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'model': [{ type: Input },],
'form': [{ type: Input },],
'fields': [{ type: Input },],
'options': [{ type: Input },],
'buildForm': [{ type: Input },],
};
}

function FormlyForm_tsickle_Closure_declarations() {
/** @type {?} */
FormlyForm.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FormlyForm.ctorParameters;
/** @type {?} */
FormlyForm.propDecorators;
/** @type {?} */
FormlyForm.prototype.model;
/** @type {?} */
FormlyForm.prototype.form;
/** @type {?} */
FormlyForm.prototype.fields;
/** @type {?} */
FormlyForm.prototype.options;
/**
 * \@internal
 * @type {?}
 */
FormlyForm.prototype.buildForm;
/** @type {?} */
FormlyForm.prototype.initialModel;
/** @type {?} */
FormlyForm.prototype.formlyBuilder;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
